/**
 * KnowledgeScreen — sibling tab of Memories inside the Memory area.
 *
 * Shows the user's Rammy-owned knowledge graph — the entities Rammy has
 * observed / linked / mentioned across every chat, Gmail sync, TMDB
 * result and SEC filing. This is the visible surface of a system that
 * would otherwise be invisible to the user.
 *
 * Design notes
 * ------------
 * • Filter chips: All / People / Companies / Places / Films / Projects
 *   / Events / Other — driven by the ``ENTITY_TYPES`` vocab.
 * • Client-side text filter over canonical_name + aliases.
 * • Each entity card shows: type icon · canonical name · a small
 *   "mentioned N times" badge · source-provenance chips (Gmail, TMDB,
 *   SEC EDGAR, Wikidata, …) · relationship_to_user tag (self /
 *   professional / social).
 * • Tapping a card slides in a detail drawer that lists every
 *   conversation where that entity was mentioned (via GET
 *   /api/conversations/search?entity_id=...). Tap a row → jump to
 *   that chat (future work — screen wiring).
 * • Empty state is warm — reassures the user their graph will build
 *   over time as they use Rammy.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import * as L from "lucide-react";
import api from "@/lib/api";

const TYPE_META = {
  all:      { label: "All",       icon: L.Layers },
  person:   { label: "People",    icon: L.Users, color: "text-sky-600" },
  company:  { label: "Companies", icon: L.Building2, color: "text-purple-600" },
  place:    { label: "Places",    icon: L.MapPin, color: "text-amber-600" },
  film:     { label: "Films",     icon: L.Film, color: "text-pink-600" },
  project:  { label: "Projects",  icon: L.Folder, color: "text-emerald-600" },
  event:    { label: "Events",    icon: L.Calendar, color: "text-orange-600" },
  other:    { label: "Other",     icon: L.Circle, color: "text-slate-500" },
};

const SOURCE_LABELS = {
  gmail: "Gmail", tmdb: "TMDB", wikidata: "Wikidata", wikipedia: "Wikipedia",
  sec_edgar: "SEC EDGAR", google_places: "Google Places", osm: "OpenStreetMap",
  crossref: "Crossref", semantic_scholar: "Semantic Scholar",
  currents: "Currents", conversation: "Chat", rammy_memory: "Rammy Memory",
  youtube: "YouTube",
};

function _fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return null; }
}


export function KnowledgeScreen() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);        // full entity doc
  const [selectedConvos, setSelectedConvos] = useState(null); // array or null while loading

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = typeFilter === "all" ? {} : { entity_type: typeFilter };
      const r = await api.get("/entities/list", { params: { ...params, limit: 300 } });
      setItems(r.data.entities || []);
    } catch (e) {
      toast.error("Couldn't load your knowledge graph.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const out = { all: items.length };
    for (const k of Object.keys(TYPE_META)) if (k !== "all") out[k] = 0;
    for (const e of items) out[e.type] = (out[e.type] || 0) + 1;
    return out;
  }, [items]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const ql = q.toLowerCase();
    return items.filter(e =>
      (e.canonical_name || "").toLowerCase().includes(ql) ||
      (e.aliases || []).some(a => (a || "").toLowerCase().includes(ql))
    );
  }, [items, q]);

  const openEntity = async (e) => {
    setSelected(e);
    setSelectedConvos(null);
    try {
      const r = await api.get("/conversations/search", {
        params: { entity_id: e.entity_id, limit: 20 },
      });
      setSelectedConvos(r.data.results || []);
    } catch (_) {
      setSelectedConvos([]);
    }
  };

  return (
    <div className="p-5 md:p-8 space-y-5 max-w-5xl mx-auto" data-testid="screen-knowledge">
      <div>
        <div className="text-2xl font-display">Knowledge</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
          People, companies, films, places — everything Rammy has noticed while helping you.
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(TYPE_META).map(([k, m]) => {
          const Icon = m.icon;
          const active = typeFilter === k;
          const c = counts[k] || 0;
          return (
            <button
              key={k}
              onClick={() => setTypeFilter(k)}
              data-testid={`kn-filter-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition ${
                active
                  ? "bg-[var(--rammy-cyan)]/15 border-[var(--rammy-cyan)] text-[var(--rammy-cyan-dark)]"
                  : "bg-[var(--rm-surface)] border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
              }`}
            >
              <Icon size={12}/>
              <span>{m.label}</span>
              {c > 0 && <span className="text-[10px] opacity-70">{c}</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <L.Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]"/>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or alias"
          className="pl-9 rounded-2xl h-10"
          data-testid="kn-search"
        />
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">Loading your graph…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--rammy-border)] p-8 text-center text-sm text-[var(--rammy-text-muted)]">
          <L.Sparkles size={24} className="mx-auto text-[var(--rammy-cyan-dark)] mb-2"/>
          {items.length === 0
            ? "Nothing here yet. Ask Rammy about a film, company, or person — I'll start remembering."
            : "No matches for that filter."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {filtered.map((e) => {
            const meta = TYPE_META[e.type] || TYPE_META.other;
            const Icon = meta.icon;
            return (
              <button
                key={e.entity_id}
                onClick={() => openEntity(e)}
                data-testid={`kn-entity-${e.entity_id}`}
                className="text-left rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-3.5 hover:border-[var(--rammy-cyan)] hover:rammy-shadow transition"
              >
                <div className="flex items-start gap-3">
                  <span className={`w-8 h-8 rounded-xl bg-[var(--rammy-surface-alt)] flex items-center justify-center shrink-0 ${meta.color}`}>
                    <Icon size={16}/>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{e.canonical_name}</span>
                      {e.relationship_to_user && e.relationship_to_user !== "self" && (
                        <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">
                          {e.relationship_to_user}
                        </span>
                      )}
                    </div>
                    {e.aliases?.length > 0 && (
                      <div className="text-[11px] text-[var(--rammy-text-muted)] truncate">
                        aka {e.aliases.slice(0, 3).join(", ")}{e.aliases.length > 3 ? "…" : ""}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {(e.sources || []).slice(0, 4).map((s) => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]">
                          {SOURCE_LABELS[s] || s}
                        </span>
                      ))}
                      {e.mention_count > 0 && (
                        <span className="text-[10px] text-[var(--rammy-text-muted)] ml-auto">
                          {e.mention_count}×
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={() => setSelected(null)} data-testid="kn-drawer">
          <div
            className="w-full sm:w-[440px] bg-[var(--rm-surface)] h-full overflow-y-auto shadow-xl border-l border-[var(--rammy-border)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[var(--rammy-border)] flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">
                  {TYPE_META[selected.type]?.label || selected.type}
                </div>
                <div className="text-lg font-medium truncate">{selected.canonical_name}</div>
                {selected.aliases?.length > 0 && (
                  <div className="text-xs text-[var(--rammy-text-muted)] mt-1">
                    aka {selected.aliases.join(", ")}
                  </div>
                )}
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] shrink-0"
                aria-label="Close"
                data-testid="kn-drawer-close"
              >
                <L.X size={18}/>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Sources block */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold mb-2">
                  How Rammy knows this
                </div>
                <div className="space-y-1.5">
                  {Object.entries(selected.source_details || {}).map(([src, info]) => (
                    <div key={src} className="flex items-center justify-between text-xs rounded-lg px-2.5 py-1.5 bg-[var(--rammy-surface-alt)]">
                      <span className="font-medium">{SOURCE_LABELS[src] || src}</span>
                      <span className="text-[var(--rammy-text-muted)]">
                        {info?.last_verified && _fmtDate(info.last_verified)}
                        {info?.confidence !== undefined && ` · ${(info.confidence * 100).toFixed(0)}%`}
                      </span>
                    </div>
                  ))}
                  {Object.keys(selected.source_details || {}).length === 0 && (
                    <div className="text-xs text-[var(--rammy-text-muted)]">No source details recorded.</div>
                  )}
                </div>
              </div>

              {/* Mention stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-[var(--rammy-border)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">Mentions</div>
                  <div className="text-2xl font-medium">{selected.mention_count || 0}</div>
                </div>
                <div className="rounded-xl border border-[var(--rammy-border)] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">Last seen</div>
                  <div className="text-sm font-medium mt-2">
                    {_fmtDate(selected.last_mentioned) || "—"}
                  </div>
                </div>
              </div>

              {/* Recent conversations */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold mb-2">
                  Mentioned in conversations
                </div>
                {selectedConvos === null ? (
                  <div className="text-xs text-[var(--rammy-text-muted)]">Loading…</div>
                ) : selectedConvos.length === 0 ? (
                  <div className="text-xs text-[var(--rammy-text-muted)]">
                    No conversation extractions yet. Rammy processes chats in the background — check back in a few minutes.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {selectedConvos.map((c) => (
                      <div key={c.conversation_id} className="rounded-lg border border-[var(--rammy-border)] p-2.5"
                           data-testid={`kn-convo-${c.conversation_id}`}>
                        <div className="text-xs font-medium">{c.summary || "Conversation"}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          {(c.topics || []).slice(0, 3).map((t) => (
                            <span key={t.topic} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]">
                              {t.topic}
                            </span>
                          ))}
                          <span className="text-[10px] text-[var(--rammy-text-muted)] ml-auto">
                            {_fmtDate(c.extracted_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
